/**
 * TỔ TRƯỞNG MỞ CA TẠI CÔNG TRÌNH — docs/02 §2.4, docs/03 B6, E8, E9.
 *
 * Mở ca (cần mạng, một lần) → nhận `totp_secret` → điện thoại TỰ SINH mã 6 số
 * mỗi 30 giây, không cần mạng nữa. Thợ gõ mã đó vào màn Chấm công (hoặc quét
 * QR — chưa làm). Mã đổi liên tục nên chụp màn hình gửi đi là vô ích (E8).
 *
 * Secret giữ trong localStorage tới khi đóng ca — để tổ trưởng tắt app mở
 * lại vẫn có mã. Đóng ca là xoá.
 */

import { useCallback, useEffect, useState } from 'react'
import QRCode from 'qrcode'

import { ApiError } from '../api/loi'
import { nhanVienHienTai } from '../services/auth.service'
import { layDiaDiem } from '../services/catalog.service'
import { dongCaCongTrinh, moCaCongTrinh } from '../services/site-session.service'
import type { CaCongTrinh as Ca, DiaDiem } from '../types'
import { gio } from '../lib/gio'
import { giayConLai, maTOTP } from '../lib/totp'

const KHOA_CA = 'chamcong.ca_dang_mo'

function docCaDaLuu(): Ca | null {
  try {
    const s = localStorage.getItem(KHOA_CA)
    return s ? (JSON.parse(s) as Ca) : null
  } catch { return null }
}

export default function CaCongTrinh() {
  const nv = nhanVienHienTai()
  const [diaDiem, setDiaDiem] = useState<DiaDiem[]>([])
  const [siteId, setSiteId] = useState<number | ''>('')
  const [ca, setCa] = useState<Ca | null>(docCaDaLuu)
  const [qr, setQr] = useState('')
  const [ma, setMa] = useState('------')
  const [conLai, setConLai] = useState(giayConLai())
  const [loi, setLoi] = useState<string | null>(null)
  const [dangXuLy, setDangXuLy] = useState(false)

  useEffect(() => {
    layDiaDiem().then((ds) => setDiaDiem(ds.filter((d) => d.kind === 'site'))).catch(() => undefined)
  }, [])

  // Sinh mã trên máy mỗi giây (mã đổi mỗi 30s, đồng hồ đếm ngược đổi mỗi giây)
  useEffect(() => {
    if (!ca?.totp_secret) return
    let huy = false
    const tick = async () => {
      const m = await maTOTP(ca.totp_secret!).catch(() => '------')
      if (!huy) { setMa(m); setConLai(giayConLai()) }
    }
    void tick()
    const t = window.setInterval(() => void tick(), 1000)
    return () => { huy = true; window.clearInterval(t) }
  }, [ca])

  useEffect(() => {
    let huy = false
    setQr('')
    if (ca && /^\d{6}$/.test(ma)) {
      void QRCode.toDataURL(JSON.stringify({ session_id: ca.id, site_id: ca.site_id, code: ma }),
        { width: 280, margin: 4, errorCorrectionLevel: 'M' })
        .then(url => { if (!huy) setQr(url) }).catch(() => { if (!huy) setLoi('Không tạo được QR. Dùng mã 6 số.') })
    }
    return () => { huy = true }
  }, [ca, ma])

  const moCa = useCallback(async () => {
    if (siteId === '') return
    setDangXuLy(true)
    setLoi(null)
    try {
      const kq = await moCaCongTrinh(Number(siteId))
      localStorage.setItem(KHOA_CA, JSON.stringify(kq))
      setCa(kq)
    } catch (e) {
      setLoi(e instanceof ApiError ? e.message : 'Không mở được ca. Kiểm tra mạng.')
    } finally {
      setDangXuLy(false)
    }
  }, [siteId])

  const dongCa = useCallback(async () => {
    if (!ca) return
    if (!window.confirm('Đóng ca? Mã sẽ ngừng hiệu lực ngay, thợ chưa chấm sẽ phải chấm bằng GPS + ảnh.')) return
    setDangXuLy(true)
    try {
      await dongCaCongTrinh(ca.id)
      localStorage.removeItem(KHOA_CA)
      setCa(null)
    } catch (e) {
      // Mất mạng: vẫn xoá ở máy — server sẽ đóng khi tổ trưởng có mạng bấm lại,
      // hoặc ca tự hết hiệu lực khi sang ngày mới (work_date khác)
      setLoi(e instanceof ApiError ? e.message : 'Chưa báo được cho hệ thống, sẽ đóng khi có mạng.')
    } finally {
      setDangXuLy(false)
    }
  }, [ca])

  if (!nv || !['supervisor', 'manager', 'admin'].includes(nv.role)) {
    return (
      <div className="man-hinh">
        <header className="dau-trang"><h1>Ca công trình</h1></header>
        <p className="ghi-chu">Chỉ tổ trưởng mới mở được ca.</p>
      </div>
    )
  }

  return (
    <div className="man-hinh ca-cong-trinh">
      <header className="dau-trang"><h1>Ca công trình</h1></header>

      {loi && <p className="thong-bao-loi" role="alert">{loi}</p>}

      {ca ? (
        <section className="khoi ma-ca">
          <h2>{ca.site_name ?? `Công trình #${ca.site_id}`}</h2>
          <p className="ghi-chu">Mở lúc {gio(ca.opened_at)} · ca #{ca.id}</p>
          {qr && <img src={qr} width={280} height={280} alt="Mã QR ca công trình" />}
          <div className="ma-lon" aria-live="polite">{ma.slice(0, 3)} {ma.slice(3)}</div>
          <div className="thanh-dem-nguoc" aria-hidden="true">
            <div className="thanh-dem-nguoc-day" style={{ width: `${(conLai / 30) * 100}%` }} />
          </div>
          <p className="huong-dan-ma">
            Thợ quét QR hoặc nhập mã trong màn Chấm công. Mã đổi sau {conLai} giây.
          </p>
          <p className="ghi-chu">
            Mã sinh ngay trên máy, không cần mạng. Thợ có mạng thì gửi ngay, không
            thì xếp hàng đợi và gửi sau — mã vẫn được chấp nhận.
          </p>
          <button type="button" className="nut-nguy-hiem" onClick={() => void dongCa()} disabled={dangXuLy}>
            Đóng ca
          </button>
        </section>
      ) : (
        <section className="khoi">
          <h2>Mở ca hôm nay</h2>
          <label className="truong">
            <span>Công trình</span>
            <select value={siteId} onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— chọn công trình —</option>
              {diaDiem.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <div className="cum-nut">
            <button type="button" className="nut-gui" disabled={siteId === '' || dangXuLy}
                    onClick={() => void moCa()}>
              {dangXuLy ? 'Đang mở…' : 'Mở ca, lấy mã'}
            </button>
          </div>
          <p className="ghi-chu">
            Cần mạng một lần để mở ca. Sau đó điện thoại tự sinh mã 30 giây/lần,
            kể cả khi mất sóng.
          </p>
        </section>
      )}
    </div>
  )
}
